import {
    Box,
    Typography,
    useTheme,
    CircularProgress,
    Alert,
    Chip,
    Theme
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Image, View } from 'react-native';
import PortalCard from '../../../../../components/PortalCard';
import { getBadgeText } from '../../../../../utils/customFunctions';
import { productAnalyticsStore } from '../../../../../app/stores/ProductAnalytics';
import { LowStockProduct } from '../../../../../models/Analytics';

// Shared Styles Configuration
const getSharedStyles = (theme: Theme) => ({
    card: {
        marginX: 1,
        backgroundColor: theme.customColors?.cardBackground || theme.palette.background.paper,
        borderRadius: 4,
        padding: 2,
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        gap: 2,
        position: 'relative' as const,
        '&:hover': {
            boxShadow: theme.shadows[8]
        }
    },
    productImage: {
        flexShrink: 0
    },
    productInfo: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 1
    },
    productName: {
        fontWeight: 'bold',
        fontFamily: 'primaryFont',
        color: theme.palette.text.primary,
        alignItems: 'flex-start'
    },
    badge: {
        borderRadius: 1,
        paddingX: 1,
        paddingY: 0.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        flexShrink: 0
    },
    badgeText: {
        fontSize: '10px',
        fontWeight: 'bold'
    },
    stockAlert: {
        py: 1,
        px: 1.5,
        minWidth: 100,
        flexShrink: 0,
        '& .MuiAlert-message': {
            fontWeight: 'bold'
        }
    },
    stockCount: {
        minWidth: 80,
        textAlign: 'right' as const,
        flexShrink: 0
    },
    container: {
        flexGrow: 1,
        overflow: 'auto',
        maxHeight: '500px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 1
    },
    lowestStockContainer: {
        flexGrow: 1,
        overflow: 'auto',
        maxHeight: '400px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 1
    },
    loadingContainer: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
});

// Helper function to get stock severity
const getStockSeverity = (stock: number, threshold: number): 'error' | 'warning' => {
    const percentage = (stock / threshold) * 100;
    return percentage <= 50 ? 'error' : 'warning';
};

// Product Card Component
interface ProductCardProps {
    product: LowStockProduct;
    isLowStock: boolean;
    styles: ReturnType<typeof getSharedStyles>;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, isLowStock, styles }) => {
    const theme = useTheme();
    const discountAmount = product.discount ? Number(product.discount) : 0;
    const isOnSale = discountAmount > 0;
    const imageSize = 75;
    const { text: badgeText, textColor, backgroundColor } = getBadgeText({
        inStock: product.inStock,
        isOnSale,
        discountAmount
    });

    const CustomAlertComponent = ({ textValue, severity, stockOnHand, stockLowThreshold }: { textValue: string, severity: 'error' | 'warning' | 'success', stockOnHand: number, stockLowThreshold: number }) => {
        return (
            <Alert
                variant='standard'
                severity={severity}
                sx={{
                    borderColor: getStockSeverity(stockOnHand, stockLowThreshold) === 'error' ? theme.palette.error.main : theme.palette.warning.main,
                    color: 'white',
                    borderWidth: 1,
                    paddingX: 1.5,
                    paddingY: 0.25,
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: 12,
                    fontFamily: 'primaryFont',
                    '& .MuiAlert-icon': {
                        fontSize: '1rem',
                        padding: 0,
                        alignSelf: 'center'
                    }
                }}
            >
                {textValue}
            </Alert>
        );
    };


    return (
        <Box sx={styles.card}>
            {/* Product Image */}
            <Box sx={styles.productImage}>
                <View style={{
                    backgroundColor: 'white',
                    overflow: 'hidden',
                    borderRadius: 10,
                    height: imageSize,
                    aspectRatio: 1 / 1
                }}>
                    <Image
                        source={product.imageUrl
                            ? { uri: product.imageUrl }
                            : require('../../../../../assets/no-image.png')}
                        style={{
                            resizeMode: 'cover',
                            width: '100%',
                            height: '100%'
                        }}
                    />
                </View>
            </Box>

            {/* Product Info */}
            <Box sx={styles.productInfo}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" fontWeight='bold' textAlign='left' sx={{ flex: 1 }}>
                        {product.productName}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        label={product.category}
                        size='small'
                        variant='filled'
                        sx={{ px: 0.5 }}
                    />
                    {badgeText && (
                        <Chip
                            label={badgeText + (product.inStock && discountAmount > 0 ? `-${discountAmount}%` : '')}
                            size='small'
                            variant='filled'
                            sx={{ px: 0.5, color: textColor, backgroundColor: backgroundColor }}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                {/* Stock Alert Badge */}
                {isLowStock && (
                    <CustomAlertComponent
                        textValue={`${product.stockOnHand} left`}
                        severity='warning'
                        stockOnHand={product.stockOnHand}
                        stockLowThreshold={product.stockLowThreshold}
                    />
                )}
                {!isLowStock && (
                    <CustomAlertComponent
                        textValue={product.stockOnHand.toString() + ' units'}
                        severity='success' 
                        stockOnHand={product.stockOnHand}
                        stockLowThreshold={product.stockLowThreshold}
                    />
                )}
            </Box>
        </Box>
    );
};

// Loading State Component
const LoadingState: React.FC<{ styles: ReturnType<typeof getSharedStyles> }> = ({ styles }) => {
    return (
        <Box sx={styles.loadingContainer}>
            <CircularProgress />
        </Box>
    );
};

// Error State Component
interface ErrorStateProps {
    error: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error }) => {
    return <Alert severity='error'>{error}</Alert>;
};

// Low Stock Products List Component
interface LowStockProductsListProps {
    products: LowStockProduct[];
    styles: ReturnType<typeof getSharedStyles>;
}

const LowStockProductsList: React.FC<LowStockProductsListProps> = ({ products, styles }) => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const [isScrollable, setIsScrollable] = useState(false);

    // Check if container is scrollable and track scroll position
    useEffect(() => {
        const checkScrollable = () => {
            if (containerRef.current) {
                const { scrollHeight, clientHeight } = containerRef.current;
                setIsScrollable(scrollHeight > clientHeight);
            }
        };

        const handleScroll = () => {
            if (containerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
                setIsScrolledFromTop(scrollTop > 10);
                setIsAtBottom(scrollHeight - scrollTop - clientHeight < 10);
            }
        };

        checkScrollable();
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            // Check initial state
            handleScroll();
        }

        window.addEventListener('resize', checkScrollable);

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
            window.removeEventListener('resize', checkScrollable);
        };
    }, [products]);

    const fadeColor = theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : 'rgba(255, 255, 255, 1)';

    return (
        <Box sx={{ position: 'relative', flexGrow: 1 }}>
            {/* Top Fade - only show when scrolled from top */}
            {isScrolledFromTop && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 40,
                        background: theme.palette.mode === 'dark'
                            ? `linear-gradient(to bottom, ${fadeColor}, transparent)`
                            : `linear-gradient(to bottom, ${fadeColor}, transparent)`,
                        pointerEvents: 'none',
                        zIndex: 1
                    }}
                />
            )}
            {/* Scrollable Content */}
            <Box ref={containerRef} sx={styles.container}>
                {products.map((product) => (
                    <ProductCard
                        key={product.productId}
                        product={product}
                        isLowStock={true}
                        styles={styles}
                    />
                ))}
            </Box>
            {/* Bottom Fade - only show when scrollable and not at bottom */}
            {isScrollable && !isAtBottom && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 40,
                        background: theme.palette.mode === 'dark'
                            ? `linear-gradient(to top, ${fadeColor}, transparent)`
                            : `linear-gradient(to top, ${fadeColor}, transparent)`,
                        pointerEvents: 'none',
                        zIndex: 1
                    }}
                />
            )}
        </Box>
    );
};

// Lowest Stock Products List Component
interface LowestStockProductsListProps {
    products: LowStockProduct[];
    styles: ReturnType<typeof getSharedStyles>;
}

const LowestStockProductsList: React.FC<LowestStockProductsListProps> = ({ products, styles }) => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const [isScrollable, setIsScrollable] = useState(false);

    // Check if container is scrollable and track scroll position
    useEffect(() => {
        const checkScrollable = () => {
            if (containerRef.current) {
                const { scrollHeight, clientHeight } = containerRef.current;
                setIsScrollable(scrollHeight > clientHeight);
            }
        };

        const handleScroll = () => {
            if (containerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
                setIsScrolledFromTop(scrollTop > 10);
                setIsAtBottom(scrollHeight - scrollTop - clientHeight < 10);
            }
        };

        checkScrollable();
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            // Check initial state
            handleScroll();
        }

        window.addEventListener('resize', checkScrollable);

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
            window.removeEventListener('resize', checkScrollable);
        };
    }, [products]);

    const fadeColor = theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : 'rgba(255, 255, 255, 1)';

    return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Alert severity='success' sx={{ mb: 1 }}>
                Showing top 10 lowest stock products
            </Alert>
            <Box sx={{ position: 'relative', flexGrow: 1 }}>
                {/* Top Fade - only show when scrolled from top */}
                {isScrolledFromTop && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 40,
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(to bottom, ${fadeColor}, transparent)`
                                : `linear-gradient(to bottom, ${fadeColor}, transparent)`,
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    />
                )}
                {/* Scrollable Content */}
                <Box ref={containerRef} sx={styles.lowestStockContainer}>
                    {products.map((product) => (
                        <ProductCard
                            key={product.productId}
                            product={product}
                            isLowStock={false}
                            styles={styles}
                        />
                    ))}
                </Box>
                {/* Bottom Fade - only show when scrollable and not at bottom */}
                {isScrollable && !isAtBottom && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 40,
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(to top, ${fadeColor}, transparent)`
                                : `linear-gradient(to top, ${fadeColor}, transparent)`,
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

// Success State Component
const SuccessState: React.FC = () => {
    return (
        <Alert severity='success'>
            All products have sufficient stock levels!
        </Alert>
    );
};

const LowStockAlert: React.FC = () => {
    const theme = useTheme();
    const styles = getSharedStyles(theme);

    // Get state and actions from store
    const {
        lowStockProducts,
        lowStockProductsLoading,
        lowStockProductsError,
        lowestStockProducts,
        lowestStockProductsLoading,
        lowestStockProductsError,
        fetchLowStockProducts,
        fetchLowestStockProducts
    } = productAnalyticsStore((state) => ({
        lowStockProducts: state.lowStockProducts,
        lowStockProductsLoading: state.lowStockProductsLoading,
        lowStockProductsError: state.lowStockProductsError,
        lowestStockProducts: state.lowestStockProducts,
        lowestStockProductsLoading: state.lowestStockProductsLoading,
        lowestStockProductsError: state.lowestStockProductsError,
        fetchLowStockProducts: state.fetchLowStockProducts,
        fetchLowestStockProducts: state.fetchLowestStockProducts
    }));

    useEffect(() => {
        const fetchData = async () => {
            // First, check for low stock products
            await fetchLowStockProducts();
        };

        fetchData();
    }, []);

    useEffect(() => {
        // If no low stock products, fetch the lowest stock products
        if (!lowStockProductsLoading && lowStockProducts.length === 0 && !lowStockProductsError) {
            fetchLowestStockProducts(10);
        }
    }, [lowStockProductsLoading, lowStockProducts.length, lowStockProductsError, fetchLowestStockProducts]);

    const hasLowStock = lowStockProducts.length > 0;
    const loading = lowStockProductsLoading || lowestStockProductsLoading;
    const error = lowStockProductsError || lowestStockProductsError;

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ textAlign: 'left', width: '100%' }}>
                    <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ textAlign: 'left' }}>
                        Low Stock Products
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'left' }}>
                        Products below stock threshold
                    </Typography>
                </Box>
            </Box>

            {loading ? (
                <LoadingState styles={styles} />
            ) : error ? (
                <ErrorState error={error} />
            ) : hasLowStock && lowStockProducts.length > 0 ? (
                <LowStockProductsList products={lowStockProducts} styles={styles} />
            ) : lowestStockProducts.length > 0 ? (
                <LowestStockProductsList products={lowestStockProducts} styles={styles} />
            ) : (
                <SuccessState />
            )}
        </PortalCard>
    );
};

export default LowStockAlert;

